import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Loader2Icon, MailCheckIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'

import { authApi } from '@/api/auth'
import { ApiError } from '@/api/errors'
import { homePathFor } from '@/auth/guards'
import { useAuthStore } from '@/auth/store'
import { AuthLayout } from '@/components/auth-layout'
import { FieldError, FormError } from '@/components/field-error'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { browserLocale, msg, setCurrentLocale } from '@/i18n'
import { portalLocale } from '@/i18n/portal-language'
import { formatDate } from '@/lib/format'
import { USER_ROLE_LABELS } from '@/lib/labels'
import { invitationNextPath } from '@/lib/portal'

const MIN_PASSWORD_LENGTH = 10

interface AcceptForm {
  name: string
  password: string
  confirmation: string
}

export const Route = createFileRoute('/invitation/$token')({
  component: InvitationPage,
})

function InvitationPage() {
  const { token } = Route.useParams()
  const invitation = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => authApi.invitationPreview(token),
    retry: false,
    staleTime: 60_000,
  })
  // Lot 34 : la langue de l'invitation (1.73.0), sinon celle du navigateur.
  setCurrentLocale(portalLocale(invitation.data?.language ?? browserLocale()))
  const t = msg().auth.invitation

  if (invitation.isPending) {
    return (
      <AuthLayout title={t.title}>
        <div className="space-y-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </AuthLayout>
    )
  }

  if (invitation.isError) {
    const expired = invitation.error instanceof ApiError && invitation.error.status === 404
    return (
      <AuthLayout title={t.invalidTitle} description={expired ? t.expired : t.unverifiable}>
        <Button asChild variant="outline" className="w-full">
          <Link to="/login" search={{ redirect: undefined }} data-testid="invitation-sign-in-link">
            {t.backToSignIn}
          </Link>
        </Button>
      </AuthLayout>
    )
  }

  return <AcceptInvitationForm token={token} preview={invitation.data} />
}

function AcceptInvitationForm({
  token,
  preview,
}: {
  token: string
  preview: Awaited<ReturnType<typeof authApi.invitationPreview>>
}) {
  const navigate = useNavigate()
  const signIn = useAuthStore((state) => state.signIn)
  const [formError, setFormError] = useState<string | null>(null)
  const nameId = useId()
  const passwordId = useId()
  const confirmationId = useId()
  const linkOnlyId = useId()
  // 1.69.0 : un contact du portail peut se passer de mot de passe ; l'équipe, non.
  const portal = preview.kind === 'portal'
  const t = msg().auth.invitation
  const [linkOnly, setLinkOnly] = useState(false)
  const withPassword = !portal || !linkOnly

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AcceptForm>({ defaultValues: { name: '', password: '', confirmation: '' } })

  // Les règles d'un champ restent attachées même démonté : sans mot de passe,
  // elles se taisent.
  const passwordRules = withPassword
    ? {
        required: t.passwordRequired,
        minLength: {
          value: MIN_PASSWORD_LENGTH,
          message: t.passwordMin(MIN_PASSWORD_LENGTH),
        },
      }
    : {}
  const confirmationRules = {
    validate: (value: string) => !withPassword || value === watch('password') || t.mismatch,
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      const session = await authApi.acceptInvitation(token, {
        name: values.name.trim(),
        ...(withPassword ? { password: values.password } : {}),
      })
      signIn(session)
      // 1.59.0 : une invitation partie avec un questionnaire mène à lui. Un
      // chemin du portail seulement, jamais une adresse d'ailleurs.
      const next = invitationNextPath(preview.next_path, session.user.kind)
      if (next) {
        await navigate({ to: next })
        return
      }
      await navigate({ to: homePathFor(session.user.kind) })
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : t.failed)
    }
  })

  return (
    <AuthLayout
      title={t.welcome}
      description={
        <span className="flex flex-col gap-2">
          <span>
            {t.invitedWith} <strong className="font-medium text-foreground">{preview.email}</strong>
            . {portal ? t.portalIntro : t.teamIntro}
          </span>
          <span className="flex flex-wrap items-center gap-2">
            {preview.role && <Badge variant="brand">{USER_ROLE_LABELS[preview.role]}</Badge>}
            {preview.client && <Badge variant="muted">{preview.client.name}</Badge>}
            <span className="text-xs text-muted-foreground">
              {t.validUntil(formatDate(preview.expires_at))}
            </span>
          </span>
        </span>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <FormError>{formError}</FormError>

        <div className="space-y-2">
          <Label htmlFor={nameId}>{t.fullName}</Label>
          <Input
            id={nameId}
            autoComplete="name"
            data-testid="invitation-name-input"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- page dédiée à un formulaire unique
            autoFocus
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${nameId}-error` : undefined}
            {...register('name', {
              required: t.nameRequired,
              minLength: { value: 2, message: t.nameTooShort },
            })}
          />
          <FieldError id={`${nameId}-error`}>{errors.name?.message}</FieldError>
        </div>

        {portal && (
          <div className="flex items-start gap-3 rounded-md border px-3 py-2">
            <Checkbox
              id={linkOnlyId}
              data-testid="invitation-link-only-checkbox"
              checked={linkOnly}
              onCheckedChange={(checked) => {
                setLinkOnly(checked === true)
              }}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor={linkOnlyId}>{t.linkOnly}</Label>
              <p className="text-xs text-muted-foreground">{t.linkOnlyHint}</p>
            </div>
          </div>
        )}

        {withPassword && (
          <>
            <div className="space-y-2">
              <Label htmlFor={passwordId}>{msg().auth.password}</Label>
              <Input
                id={passwordId}
                type="password"
                data-testid="invitation-password-input"
                autoComplete="new-password"
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? `${passwordId}-error` : `${passwordId}-hint`}
                {...register('password', passwordRules)}
              />
              {errors.password ? (
                <FieldError id={`${passwordId}-error`}>{errors.password.message}</FieldError>
              ) : (
                <p id={`${passwordId}-hint`} className="text-xs text-muted-foreground">
                  {t.passwordHint(MIN_PASSWORD_LENGTH)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={confirmationId}>{t.confirmation}</Label>
              <Input
                id={confirmationId}
                type="password"
                data-testid="invitation-confirmation-input"
                autoComplete="new-password"
                aria-invalid={!!errors.confirmation}
                aria-describedby={errors.confirmation ? `${confirmationId}-error` : undefined}
                {...register('confirmation', confirmationRules)}
              />
              <FieldError id={`${confirmationId}-error`}>{errors.confirmation?.message}</FieldError>
            </div>
          </>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          data-testid="invitation-submit-btn"
        >
          {isSubmitting ? <Loader2Icon className="animate-spin" /> : <MailCheckIcon />}
          {t.activate}
        </Button>
      </form>
    </AuthLayout>
  )
}
